import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { BalanceSheetGroup } from '@accounting/shared';
import { useBalanceSheet } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import type { DrillTarget } from '../../components/reports/DrilldownModal';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { AsOfPicker } from '../../components/reports/PeriodFilter';
import { ComparePicker } from '../../components/reports/ComparePicker';
import { RefreshButton } from '../../components/reports/RefreshButton';
import { ActiveOnlyToggle, useActiveOnly } from '../../components/reports/ActiveOnlyToggle';
import { formatAccountingDate, formatCurrency, formatPctOfTotal, todayIso } from '../../lib/formatters';
import { collectParentIds, computeParentRanges } from '../../lib/reportTree';
import {
  computeComparePeriod,
  formatMoneyDelta,
  formatPctChange,
  type ComparePreset,
} from '../../lib/comparePeriod';

export const Route = createFileRoute('/_auth/reports/balance-sheet')({
  component: BalanceSheetPage,
});

const lineCodes = (l: { code: string; codes?: string[] }) =>
  l.codes ?? (l.code ? [l.code] : []);
const groupCodes = (group: BalanceSheetGroup) =>
  group.sections.flatMap((s) => s.lines.flatMap(lineCodes));

function BalanceSheetPage() {
  const selection = useActiveSelection();
  const navigate = useNavigate();
  const [asOf, setAsOf] = useState(todayIso());
  const [activeOnly, setActiveOnly] = useActiveOnly();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [comparePreset, setComparePreset] = useState<ComparePreset>('none');
  const { data, isLoading, error, refetch, isFetching } = useBalanceSheet(selection, asOf, activeOnly);
  const comparePeriod = useMemo(
    () => computeComparePeriod(comparePreset, { asOf }),
    [comparePreset, asOf],
  );
  const { data: compareData } = useBalanceSheet(selection, comparePeriod?.asOf ?? '', activeOnly);
  const compareOn = !!comparePeriod && !!compareData;

  // Map<accountId, amount> across all groups in the compare period for O(1) lookup.
  const compareById = useMemo(() => {
    const m = new Map<string, string>();
    if (!compareData) return m;
    for (const g of [compareData.assets, compareData.liabilities, compareData.equity]) {
      for (const s of g.sections) {
        for (const l of s.lines) {
          if (l.accountId) m.set(l.accountId, l.amount);
        }
      }
    }
    return m;
  }, [compareData]);

  /** Find the compare section that matches the current section, by key. */
  function findCompareSection(group: 'assets' | 'liabilities' | 'equity', key: string): BalanceSheetGroup['sections'][number] | undefined {
    if (!compareData) return undefined;
    return compareData[group].sections.find((s) => s.key === key);
  }

  const openDrilldown = (target: DrillTarget): void =>
    void navigate({
      to: '/reports/drilldown',
      search: {
        codes: target.codes.join(','),
        label: target.label,
        to: asOf,
        back: '/reports/balance-sheet',
      },
    });

  const toggleCollapsed = (accountId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });

  // Every parent account that has children, across all three groups. Drives the
  // Expand-all / Collapse-all toolbar and hides them entirely when nothing nests.
  const allParentIds = useMemo(() => {
    if (!data) return [] as string[];
    const ids: string[] = [];
    for (const g of [data.assets, data.liabilities, data.equity]) {
      for (const s of g.sections) ids.push(...collectParentIds(s.lines));
    }
    return ids;
  }, [data]);

  if (!selection) return <NoCompany />;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>
        ← All reports
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Balance Sheet
          {selection === CONSOLIDATED && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {allParentIds.length > 0 && (
            <>
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setCollapsed(new Set(allParentIds))}
                title="Hide every parent's children; show only the rolled-up totals"
              >
                Collapse all
              </button>
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setCollapsed(new Set())}
                title="Show every parent's children"
              >
                Expand all
              </button>
            </>
          )}
          <AsOfPicker asOf={asOf} onChange={setAsOf} />
          <ActiveOnlyToggle value={activeOnly} onChange={setActiveOnly} />
          <ComparePicker value={comparePreset} onChange={setComparePreset} mode="point_in_time" />
          <RefreshButton onRefresh={() => void refetch()} isFetching={isFetching} />
          <ExportButtons selection={selection} type="balance_sheet" params={activeOnly ? { asOf, activeOnly: 'true' } : { asOf }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <div className="card" style={{ padding: 20 }}>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            As of {formatAccountingDate(data.asOf)}
          </p>

          {/* Anchor for the % column = total assets (which equals L+E for a balanced BS). */}
          {compareOn && comparePeriod && (
            <CompareHeader currentLabel={formatAccountingDate(data.asOf)} compareLabel={comparePeriod.label} />
          )}
          <GroupBlock title="Assets" group={data.assets} compareGroup={compareData?.assets} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={data.totals.assets} compareAnchor={compareData?.totals.assets} compareById={compareById} findCompareSection={(k) => findCompareSection('assets', k)} compareOn={compareOn} />
          <GroupBlock title="Liabilities" group={data.liabilities} compareGroup={compareData?.liabilities} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={data.totals.assets} compareAnchor={compareData?.totals.assets} compareById={compareById} findCompareSection={(k) => findCompareSection('liabilities', k)} compareOn={compareOn} />
          <GroupBlock title="Equity" group={data.equity} compareGroup={compareData?.equity} onDrill={openDrilldown} collapsed={collapsed} onToggle={toggleCollapsed} anchor={data.totals.assets} compareAnchor={compareData?.totals.assets} compareById={compareById} findCompareSection={(k) => findCompareSection('equity', k)} compareOn={compareOn} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '2px solid var(--border)',
              marginTop: 12,
              paddingTop: 10,
              fontWeight: 700,
            }}
          >
            <span>Liabilities + Equity</span>
            <span>
              {formatCurrency(
                (parseFloat(data.totals.liabilities) + parseFloat(data.totals.equity)).toFixed(2),
              )}
            </span>
          </div>

          <div style={{ marginTop: 12 }}>
            {data.balanced ? (
              <span className="tag ok">balanced</span>
            ) : (
              <span className="tag bad">OUT OF BALANCE</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

/** Right-aligned cell carrying current amount + % of total, optionally a compare amount + delta. */
function AmountPctCell({
  amount,
  pct,
  bold,
  blankAmount,
  compareAmount,
  comparePct,
  compareOn,
  onClick,
}: {
  amount: string;
  pct: string;
  bold?: boolean;
  /** When true, render the amount column blank instead of "$0.00". For parent / header rows
   *  that have no direct posting — distinguishes "never had activity" from "netted to zero". */
  blankAmount?: boolean;
  compareAmount?: string;
  comparePct?: string;
  compareOn?: boolean;
  onClick?: () => void;
}) {
  const fw = bold ? 600 : undefined;
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      {blankAmount ? (
        <span style={{ fontWeight: fw, minWidth: 0 }} />
      ) : onClick ? (
        <button className="amount-link" style={{ fontWeight: fw }} onClick={onClick}>
          {formatCurrency(amount)}
        </button>
      ) : (
        <span style={{ fontWeight: fw }}>{formatCurrency(amount)}</span>
      )}
      <span className="muted" style={{ minWidth: 56, textAlign: 'right', fontSize: 12 }}>
        {blankAmount ? '' : pct}
      </span>
      {compareOn && (
        <>
          <span style={{ minWidth: 100, textAlign: 'right', fontWeight: fw, color: 'var(--text-muted)' }}>
            {compareAmount !== undefined ? formatCurrency(compareAmount) : '—'}
          </span>
          <span className="muted" style={{ minWidth: 56, textAlign: 'right', fontSize: 12 }}>
            {comparePct ?? ''}
          </span>
          <span style={{ minWidth: 110, textAlign: 'right', fontSize: 12 }}>
            {compareAmount !== undefined ? formatMoneyDelta(amount, compareAmount) : ''}
          </span>
          <span style={{ minWidth: 64, textAlign: 'right', fontSize: 12 }}>
            {compareAmount !== undefined ? formatPctChange(amount, compareAmount) : ''}
          </span>
        </>
      )}
    </div>
  );
}

function CompareHeader({ currentLabel, compareLabel }: { currentLabel: string; compareLabel: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingBottom: 4,
        marginBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span />
      <div style={{ display: 'flex', gap: 12 }}>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{currentLabel}</span>
        <span style={{ minWidth: 56, textAlign: 'right' }}>%</span>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{compareLabel}</span>
        <span style={{ minWidth: 56, textAlign: 'right' }}>%</span>
        <span style={{ minWidth: 110, textAlign: 'right' }}>$ Δ</span>
        <span style={{ minWidth: 64, textAlign: 'right' }}>% Δ</span>
      </div>
    </div>
  );
}

function GroupBlock({
  title,
  group,
  compareGroup,
  onDrill,
  collapsed,
  onToggle,
  anchor,
  compareAnchor,
  compareById,
  findCompareSection,
  compareOn,
}: {
  title: string;
  group: BalanceSheetGroup;
  compareGroup?: BalanceSheetGroup;
  onDrill: (t: DrillTarget) => void;
  collapsed: Set<string>;
  onToggle: (accountId: string) => void;
  anchor: string;
  compareAnchor?: string;
  compareById: Map<string, string>;
  findCompareSection: (key: string) => BalanceSheetGroup['sections'][number] | undefined;
  compareOn: boolean;
}) {
  const allCodes = groupCodes(group);
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {title}
      </h2>
      {group.sections.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>None</div>
      )}
      {group.sections.map((s) => (
        <div key={s.key} style={{ marginBottom: 8 }}>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 0 2px' }}>
            {s.label}
          </div>
          <SectionLines
            section={s}
            compareSection={findCompareSection(s.key)}
            onDrill={onDrill}
            collapsed={collapsed}
            onToggle={onToggle}
            anchor={anchor}
            compareAnchor={compareAnchor}
            compareById={compareById}
            compareOn={compareOn}
          />
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
        <span>Total {title}</span>
        <AmountPctCell
          amount={group.total}
          pct={formatPctOfTotal(group.total, anchor)}
          bold
          compareAmount={compareGroup?.total}
          comparePct={compareGroup && compareAnchor ? formatPctOfTotal(compareGroup.total, compareAnchor) : ''}
          compareOn={compareOn}
          onClick={allCodes.length > 0 ? () => onDrill({ codes: allCodes, label: `Total ${title}` }) : undefined}
        />
      </div>
    </div>
  );
}

function SectionLines({
  section,
  compareSection,
  onDrill,
  collapsed,
  onToggle,
  anchor,
  compareAnchor,
  compareById,
  compareOn,
}: {
  section: BalanceSheetGroup['sections'][number];
  compareSection?: BalanceSheetGroup['sections'][number];
  onDrill: (t: DrillTarget) => void;
  collapsed: Set<string>;
  onToggle: (accountId: string) => void;
  anchor: string;
  compareAnchor?: string;
  compareById: Map<string, string>;
  compareOn: boolean;
}) {
  const parents = useMemo(() => computeParentRanges(section.lines), [section.lines]);

  const rows: React.ReactNode[] = [];
  let skipUntil = -1;
  section.lines.forEach((l, i) => {
    if (i <= skipUntil) return;
    const indent = (l.depth ?? 0) * 16;

    if (l.isSubtotal) {
      const cmp = compareSection?.lines.find(
        (cl) => cl.isSubtotal && (cl.depth ?? 0) === (l.depth ?? 0) && cl.name === l.name,
      )?.amount;
      rows.push(
        <div key={`${section.key}-sub-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontWeight: 600 }}>
          <span style={{ paddingLeft: indent + 14 /* align under the chevron column */ }}>Total {l.name}</span>
          <AmountPctCell
            amount={l.amount}
            pct={formatPctOfTotal(l.amount, anchor)}
            bold
            blankAmount={l.hasActivity === false}
            compareAmount={cmp}
            comparePct={cmp !== undefined && compareAnchor ? formatPctOfTotal(cmp, compareAnchor) : ''}
            compareOn={compareOn}
          />
        </div>,
      );
      return;
    }

    const info = parents.get(i);
    const isParent = !!info;
    const isCollapsed = isParent && collapsed.has(l.accountId);
    if (isCollapsed) skipUntil = info!.subtotalIdx; // hide children + subtotal
    const displayAmount = isCollapsed ? info!.rolledUp : l.amount;
    const cmp = l.accountId ? compareById.get(l.accountId) : undefined;
    const codes = lineCodes(l);
    const label = l.code ? `${l.code} — ${l.name}` : l.name;

    const chevron = isParent ? (
      <button
        type="button"
        onClick={() => onToggle(l.accountId)}
        title={isCollapsed ? `Expand ${l.name}` : `Collapse ${l.name}`}
        aria-label={isCollapsed ? `Expand ${l.name}` : `Collapse ${l.name}`}
        className="muted"
        style={{
          background: 'none', border: 0, cursor: 'pointer', padding: 0,
          marginRight: 4, fontSize: 10, lineHeight: 1, width: 10, display: 'inline-block', textAlign: 'center',
        }}
      >
        {isCollapsed ? '▶' : '▼'}
      </button>
    ) : (
      // Reserve the same width so non-parent rows align with parents.
      <span style={{ display: 'inline-block', width: 10, marginRight: 4 }} />
    );

    rows.push(
      <div key={l.accountId || `${section.key}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
        <span style={{ paddingLeft: indent }}>{chevron}{label}</span>
        <AmountPctCell
          amount={displayAmount}
          pct={formatPctOfTotal(displayAmount, anchor)}
          blankAmount={!isCollapsed && l.hasActivity === false}
          compareAmount={cmp}
          comparePct={cmp !== undefined && compareAnchor ? formatPctOfTotal(cmp, compareAnchor) : ''}
          compareOn={compareOn}
          onClick={codes.length > 0 ? () => onDrill({ codes, label }) : undefined}
        />
      </div>,
    );
  });

  return <>{rows}</>;
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
